import ActionPopupForProjects from 'components/action_popup/action_popup.compoenent';
import DownloadProjectImages from 'components/download_project_images/download_project_images.component';
import PriceQuoteCard from 'components/price_quote_card/price_quote_card.component';
import TargetRenewalCard from 'components/target_renewal_card/target_renewal_card.component';
import RoundIconButton from 'common_components/ui/round_icon_button/round_icon_button.ui';
import ViewTrackingCard from 'components/view_tracking_card/view_tracking_card.component';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setProject } from 'store/reducers/projectSlice';
import { RootState } from 'store/store';
import { STRINGS } from 'constants/strings.constant';
import { loadRazorpayScript } from 'adapters/razorpay/razorpay.adapter';
import { QuoteTargetInfo } from 'interfaces/payment_modal.interface';
import { ProjectTargetRecord, RowAction } from 'interfaces/project.interface';
import { canDownloadProjectImages, countTargetImages, getProjectArLink, getProjectStatusKey, getQuoteTargetsById, handlePayment, hasTargetImage, isPublishSuccessful, isUnlimitedUser, toastifyError, toastifySuccess, useSetState } from 'utils/functions.utils';
import { Assets, Colors, FontFamily, Models, PrimaryButton, PublishedArProjectCard } from 'utils/imports.utils';
import { ButtonSize, ButtonTypes, ProjectStatus } from 'utils/interface.utils';

const ProjectList = ({
  project,
  handleProjectClick,
  handleDeleteClick,
}: {
  project: any;
  handleProjectClick: (proj: any) => void;
  handleDeleteClick: (e: React.MouseEvent, projectId: string) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [state, setState] = useSetState({
    isActionPopupOpen: false,
    selectedProjectId: '',
    isPublishedModalOpen: false,
    activity: false,
    isRazorpayLoaded: false,
    isCurrentProjectPublished: false,
    listOfTargetImageIds: [],
    targetInfoById: {} as Record<string, QuoteTargetInfo>,
    hasTargetImage: false,
    targetImageCount: 0,
    // The urls of the targets that have an image, for "Download images".
    targetImages: [] as string[],
    isDownloadingImages: false,
    // True once the targets request has answered, whether or not it found any. hasTargetImage alone can't
    // tell "no targets" from "not fetched yet".
    isTargetsFetchDone: false,
    isPriceQuoteOpen: false,
    priceQuote: null as { amount: number; currency: string; target_breakdown: any[] } | null,
    isRenewalCardOpen: false,
    isViewTrackingOpen: false,
  });

  const { currentUser } = useSelector((state: RootState) => state.user);

  const handleEdit = () => {
    dispatch(
      setProject({
        project_id: project._id,
        project_name: project.project_name,
      }),
    );
    navigate('/editor');
  };

  const handleDelete = (e: React.MouseEvent) => {
    handleDeleteClick(e, project._id);
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(
      setProject({
        project_id: project._id,
        project_name: project.project_name,
      }),
    );
    navigate(`/preview/${project._id}`);
  };

  // The exempt account (see isUnlimitedUser) is never asked to pay: the server publishes the project and marks
  // it paid, so there is no price quote and no checkout.
  const publishProjectFree = async () => {
    try {
      const res: unknown = await Models.auth.publishProject({ project_id: project._id });
      if (isPublishSuccessful(res)) {
        setState({ isPublishedModalOpen: true, isCurrentProjectPublished: true });
        toastifySuccess(STRINGS.project.publishSuccess);
      } else {
        toastifyError(STRINGS.project.publishFailed);
      }
    } catch (error) {
      console.error('Error publishing project:', error);
      toastifyError(STRINGS.project.publishFailed);
    }
  }

  const publishProject = async () => {
    if (!project._id || state.listOfTargetImageIds.length === 0) return;
    setState({ activity: true });
    if (isUnlimitedUser()) {
      await publishProjectFree();
      setState({ activity: false });
      return;
    }
    try {
      const quote: any = await Models.auth.getPriceQuote({
        project_id: project._id,
        target_ids: state.listOfTargetImageIds,
      });
      if (quote && quote.data) {
        setState({ priceQuote: quote.data, isPriceQuoteOpen: true });
      } else {
        toastifyError('Failed to fetch price quote');
      }
    } catch (error) {
      console.error('Error fetching price quote:', error);
      toastifyError('Failed to fetch price quote');
    } finally {
      setState({ activity: false });
    }
  }

  const confirmPublishProject = async () => {
    setState({ activity: true });
    try {
      await handlePayment(currentUser, project._id, state.listOfTargetImageIds, state, setState);
    } catch (error) {
      console.error('Error creating payment order:', error);
    } finally {
      setState({ activity: false, isPriceQuoteOpen: false });
    }
  }

  // const publishProject = async () => {
  //   if (!project._id) return;
  //   setState({ activity: true });
  //   try {
  //     const res: any = await Models.auth.publishProject({
  //       project_id: project._id,
  //     });

  //     if (res && res.data) {
  //       setState({ activity: false, isPublishedModalOpen: true });
  //       toastifySuccess('Published Successfully!');
  //     } else {
  //       toastifyError('Published Failed');
  //       console.error('Failed to create project data');
  //     }
  //   } catch (error) {
  //     console.error('Error creating project data:', error);
  //   } finally {
  //     setState({ activity: false });
  //   }
  // };
  const getAllProjectsData = async () => {
    if (!project._id) return;
    try {
        const res: any = await Models.auth.getManyProjectData({
            project_id: project._id,
        });
        if (res && res.data) {
          // Real Target._id (set on each item as targetId once compile succeeds), not the
          // free-form editor id -- this is what the server's payment endpoints accept.
          const targetImageIds = res.data.targets
            .map((target:any) => target.data.targetId)
            .filter(Boolean);
          setState({
            listOfTargetImageIds: targetImageIds,
            targetInfoById: getQuoteTargetsById(res.data.targets),
            hasTargetImage: hasTargetImage(res.data.targets),
            targetImageCount: countTargetImages(res.data.targets),
            targetImages: res.data.targets
              .map((target: ProjectTargetRecord) => target.data.targetImage)
              .filter((url: string | null): url is string => Boolean(url)),
            isTargetsFetchDone: true,
          })
        } else {
            console.error('No project data found');
        }
    } catch (error) {
        console.error('Error fetching project data:', error);
    }
  };

  const getCurrentProjectData = async () => {
    if (!project._id) return;
    try {
      const res: any = await Models.auth.getProject({ project_id: project._id });
      if (res && res.data) {
        setState({ isCurrentProjectPublished: res.data.project_status === ProjectStatus.PUBLISHED })
      } else {
        console.error('No project data found');
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  }

  useEffect(() => {
    getAllProjectsData();
    getCurrentProjectData();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadScript = async () => {
      const isLoaded = await loadRazorpayScript();
      if (isMounted && isLoaded) setState({ isRazorpayLoaded: true });
    };
    loadScript();
    return () => {
      isMounted = false;
    };
  }, []);

  const statusKey = getProjectStatusKey(state.isCurrentProjectPublished, state.hasTargetImage);
  const statusLabel = STRINGS.project.status[statusKey];
  const statusSubtitle = STRINGS.project.subtitle[statusKey];

  // The row's secondary actions, in display order. An action that doesn't apply to this project is left out.
  const rowActions: RowAction[] = [
    { label: STRINGS.project.rowActions.edit, icon: Assets.row_edit, onClick: handleEdit },
    // Unlike Manage expiry, this is not gated on payment_status -- an unpaid project's targets still accrue views,
    // and their owner should be able to read the counts.
    { label: STRINGS.project.rowActions.viewTracking, icon: Assets.row_view, onClick: () => setState({ isViewTrackingOpen: true }) },
    // The same rule as the preview screen's button: paid, published and an image to download. Unlike there, an
    // ineligible project just gets no button, instead of a locked one.
    ...(canDownloadProjectImages(
      project.payment_status === true,
      state.isCurrentProjectPublished ? ProjectStatus.PUBLISHED : '',
      state.targetImages.length,
    )
      ? [{ label: STRINGS.project.rowActions.downloadImages, icon: Assets.row_download, onClick: () => setState({ isDownloadingImages: true }) }]
      : []),
  ];

  return (
    <div
      className="project_list_container"
      role="row"
      onClick={() => handleProjectClick(project)}>
      <div className="project_list_container_details" role="cell">
        <div
          className={`project_list_container_details_image ${
            project?.target_image ? '' : 'project_list_container_details_image_empty'
          }`}>
          {project?.target_image && (
            <img
              src={project.target_image}
              alt="image"
              className='project_list_container_details_image_image'
            />
          )}
          {!project?.target_image && state.isTargetsFetchDone && !state.hasTargetImage && (
            <img src={Assets.dummy_target} alt="image" className='project_list_container_details_image_placeholder' />
          )}
        </div>
        <div className="project_list_container_details_text">
          <div className="project_list_container_details_title">
            {project.project_name}
          </div>
          <div className="project_list_container_details_subtitle">{statusSubtitle}</div>
        </div>
      </div>
      <div className="project_list_container_status" role="cell">
        {state.isTargetsFetchDone && (
          <span className={`project_list_container_status_pill project_list_container_status_pill_${statusKey}`}>
            <span className="project_list_container_status_dot" />
            {statusLabel}
          </span>
        )}
      </div>
      <div className="project_list_container_targets" role="cell">
        {state.isTargetsFetchDone && (
          <>
            <span className="project_list_container_targets_count">{state.targetImageCount}</span>{' '}
            <span className="project_list_container_targets_label">
              {state.targetImageCount === 1 ? STRINGS.project.targetSingular : STRINGS.project.targetPlural}
            </span>
          </>
        )}
      </div>
      <div className="project_list_container_date" role="cell">
        {new Date(project.updated_at ?? project.created_at).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
        })}
      </div>
      <div className="nav_bar_actions_buttons_container project_list_container_actions" role="cell">
        <div className="project_list_container_icon_actions">
          {rowActions.map((action) => (
            <RoundIconButton
              key={action.label}
              className="project_list_container_icon_button"
              icon={action.icon}
              label={action.label}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
            />
          ))}
        </div>
        {state.isCurrentProjectPublished && (
          <div className="nav_bar_actions_buttons_preview">
            <PrimaryButton
              text="Preview"
              left_icon={Assets.btn_phone}
              iconSize={{ width: 16, height: 16 }}
              fontFamily={FontFamily.medium}
              type={ButtonTypes.OUTLINED}
              size={ButtonSize.MEDIUM}
              onClick={handlePreview}
              activity={state.activity}
              style={{
                border: `1px solid ${Colors.border}`,
                borderRadius: '999px',
                backgroundColor: Colors.white,
              }}
            />
          </div>
        )}

        {!state.isCurrentProjectPublished && state.isTargetsFetchDone && !state.hasTargetImage && (
          <div className="nav_bar_actions_buttons_publish">
            <PrimaryButton
              text={STRINGS.project.addTargets}
              left_icon={Assets.btn_plus}
              iconSize={{ width: 16, height: 16 }}
              fontFamily={FontFamily.medium}
              type={ButtonTypes.OUTLINED}
              size={ButtonSize.MEDIUM}
              style={{
                border: `1px solid ${Colors.border}`,
                borderRadius: '999px',
                backgroundColor: Colors.white,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
            />
          </div>
        )}

        {!state.isCurrentProjectPublished && state.hasTargetImage && (
          <div className="nav_bar_actions_buttons_publish">
            <PrimaryButton
              text="Publish"
              left_icon={Assets.btn_publish}
              iconSize={{ width: 16, height: 16 }}
              fontFamily={FontFamily.medium}
              type={ButtonTypes.PRIMARY}
              size={ButtonSize.MEDIUM}  
              onClick={(e) => {
                e.stopPropagation();
                publishProject();
              }}
              activity={state.activity}
            />
          </div>
        )}
        <div
          className="project_list_container_action_popup_wrapper"
          style={{ position: 'relative' }}>
          <div
            className="project_list_container_action_icon"
            onClick={(e) => {
              e.stopPropagation();
              setState({
                isActionPopupOpen:
                  state.selectedProjectId !== project._id ||
                  !state.isActionPopupOpen,
                selectedProjectId: project._id,
              });
            }}>
            <img src={Assets.more_dots} alt="Actions" />
          </div>

          {state.isActionPopupOpen &&
            state.selectedProjectId === project._id && (
              <div
                className="action_popup_dropdown"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  zIndex: 10,
                }}>
                <ActionPopupForProjects
                  data={project}
                  handleDelete={handleDelete}
                  // Expiry and renewal only mean anything once the project has been paid for: an unpaid project's
                  // targets carry no expires_at, and create_renewal_order rejects an unpaid target outright. Absent
                  // payment_status reads as unpaid, so a response that omits the field never opens a dead end.
                  handleManageExpiry={project.payment_status === true ? (e) => {
                    e.stopPropagation();
                    setState({
                      isActionPopupOpen: false,
                      selectedProjectId: '',
                      isRenewalCardOpen: true,
                    });
                  } : undefined}
                  onClose={() =>
                    setState({
                      isActionPopupOpen: false,
                      selectedProjectId: '',
                    })
                  }
                />
              </div>
            )}

          {/* Lives here, not in the popup: the popup unmounts the moment its entry is clicked, and the download is
              async. Renders nothing visible; it downloads once on mount, then hands control back. */}
          {state.isDownloadingImages && (
            <DownloadProjectImages
              headless
              isEnabled
              projectName={project.project_name}
              targetImages={state.targetImages}
              arLink={getProjectArLink(project._id)}
              onFinished={() => setState({ isDownloadingImages: false })}
            />
          )}

          {state.isViewTrackingOpen && (
            <ViewTrackingCard
              isOpen={state.isViewTrackingOpen}
              onClose={() => setState({ isViewTrackingOpen: false })}
              projectId={project._id}
            />
          )}

          {state.isPublishedModalOpen && (
            <PublishedArProjectCard
              isOpen={state.isPublishedModalOpen}
              onClose={() => setState({ isPublishedModalOpen: false })}
              projectId={project._id}
            />
          )}

          {state.isPriceQuoteOpen && state.priceQuote && (
            <PriceQuoteCard
              isOpen={state.isPriceQuoteOpen}
              onClose={() => setState({ isPriceQuoteOpen: false })}
              onConfirm={confirmPublishProject}
              amount={state.priceQuote.amount}
              currency={state.priceQuote.currency}
              targetBreakdown={state.priceQuote.target_breakdown}
              isConfirming={state.activity}
              projectName={project.project_name}
              targetInfo={state.targetInfoById}
            />
          )}

          {state.isRenewalCardOpen && (
            <TargetRenewalCard
              isOpen={state.isRenewalCardOpen}
              onClose={() => setState({ isRenewalCardOpen: false })}
              targetIds={state.listOfTargetImageIds}
              currentUser={currentUser}
              isRazorpayLoaded={state.isRazorpayLoaded}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectList;
